import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/repricing";

const money = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};

const percent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : "";
};

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const stageLabel = (stage) =>
  ({
    no_listings: "No listings",
    needs_revenue_data: "Needs revenue data",
    sold_through: "Sold through",
    capital_recovered_tail: "Capital recovered tail",
    capital_recovered: "Capital recovered",
    stale_remaining_review: "Stale remaining review",
    recovering: "Recovering",
    listed: "Listed",
  }[stage] || stage || "Review");

const stageColor = (stage) => {
  if (stage === "sold_through" || stage === "capital_recovered") return "success";
  if (stage === "capital_recovered_tail" || stage === "recovering") return "primary";
  if (stage === "stale_remaining_review" || stage === "needs_revenue_data") return "warning";
  return "default";
};

const getInitialPoFilter = () => {
  const params = new URLSearchParams(window.location.search || "");
  return params.get("po_id") || params.get("purchase_order_id") || "";
};

export default function RepricingPoReview({ onOpenTab }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [poFilter, setPoFilter] = useState(getInitialPoFilter);
  const [creatingRunForPo, setCreatingRunForPo] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/po-performance?limit=250`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load PO performance");
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSummary(data.summary || null);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setWarnings([]);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);


  const createPoCleanupRun = useCallback(async (poId) => {
    if (!poId) return;
    setCreatingRunForPo(String(poId));
    setNotice("");
    setError("");
    try {
      const res = await fetch(`${API_BASE}/runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "po_cleanup",
          filters_json: {
            purchase_order_id: Number(poId),
            requires_purchase_order: true,
            include_blocked: true,
          },
          rules_json: {
            recommendation_version: "v1",
            review_signals: ["review_tail_discount"],
            include_all_remaining_po_rows: true,
            source_context: "purchase_orders",
            no_ebay_mutation: true,
            no_listing_price_mutation: true,
          },
          notes: `PO Cleanup from Purchase Orders/Repricing for PO #${poId}`,
          created_by: "operator",
          limit: 500,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to create PO cleanup run");
      }
      const counts = data.candidate_counts || {};
      setNotice(`${data.created ? "Created" : "Opened existing"} PO Cleanup run for PO #${poId}. ${data.recommendations_persisted || 0} row(s) ready: ${counts.auto_recommendation_candidate_count || 0} auto, ${counts.manual_review_candidate_count || 0} manual review, ${counts.blocked_candidate_count || 0} blocked.`);
      onOpenTab?.("runs", { run_id: data.run?.id, po_id: poId });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreatingRunForPo("");
    }
  }, [onOpenTab]);

  const filteredRows = useMemo(() => {
    if (!poFilter) return rows;
    return rows.filter((row) => String(row.purchase_order_id) === String(poFilter));
  }, [poFilter, rows]);

  const columns = useMemo(
    () => [
      {
        field: "purchase_order_id",
        headerName: "PO",
        width: 90,
      },
      {
        field: "supplier_name",
        headerName: "Supplier",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "po_stage",
        headerName: "Stage",
        minWidth: 180,
        renderCell: (params) => (
          <Chip
            size="small"
            color={stageColor(params.value)}
            label={stageLabel(params.value)}
          />
        ),
      },
      {
        field: "listing_count",
        headerName: "Listings",
        width: 95,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "sold_units",
        headerName: "Sold",
        width: 90,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "sell_through_pct",
        headerName: "Sell-through",
        width: 125,
        renderCell: (params) => percent(params.value),
      },
      {
        field: "po_cost",
        headerName: "PO cost",
        width: 120,
        renderCell: (params) => money(params.value),
      },
      {
        field: "listed_value",
        headerName: "Listed value",
        width: 130,
        renderCell: (params) => money(params.value),
      },
      {
        field: "net_revenue_estimate",
        headerName: "Net revenue est.",
        width: 145,
        renderCell: (params) => money(params.value),
      },
      {
        field: "capital_recovered_pct",
        headerName: "Capital recovered",
        width: 155,
        renderCell: (params) => percent(params.value),
      },
      {
        field: "remaining_stock_count",
        headerName: "Remaining total",
        width: 115,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "remaining_listed_count",
        headerName: "Remaining listed",
        width: 130,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "remaining_stock_value",
        headerName: "Remaining value",
        width: 150,
        renderCell: (params) => money(params.value),
      },
      {
        field: "stale_ebay_sync_remaining_count",
        headerName: "Stale eBay sync",
        width: 150,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "po_auto_candidate_count",
        headerName: "Auto rows",
        width: 105,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "po_manual_review_count",
        headerName: "Manual rows",
        width: 120,
        renderCell: (params) => numberText(params.value),
      },
      {
        field: "suggested_po_action",
        headerName: "Suggested action",
        minWidth: 210,
        renderCell: (params) => (
          <Chip
            size="small"
            color={params.value?.severity || "default"}
            label={params.value?.label || "Review"}
          />
        ),
      },
      {
        field: "repricing_actions",
        headerName: "Actions",
        width: 210,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={creatingRunForPo === String(params.row.purchase_order_id)}
              onClick={() => createPoCleanupRun(params.row.purchase_order_id)}
            >
              {creatingRunForPo === String(params.row.purchase_order_id) ? "Creating" : "PO Run"}
            </Button>
            <Button size="small" variant="outlined" onClick={() => setPoFilter(String(params.row.purchase_order_id))}>
              Filter
            </Button>
          </Stack>
        ),
      },
      {
        field: "data_quality_warnings",
        headerName: "Data warnings",
        flex: 1,
        minWidth: 190,
        sortable: false,
        renderCell: (params) => {
          const values = Array.isArray(params.value) ? params.value : [];
          if (!values.length) return <Typography variant="body2">-</Typography>;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {values.slice(0, 2).map((warning) => (
                <Chip key={warning} size="small" variant="outlined" label={warning} />
              ))}
              {values.length > 2 && <Chip size="small" label={`+${values.length - 2}`} />}
            </Stack>
          );
        },
      },
    ],
    [createPoCleanupRun, creatingRunForPo]
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            PO Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only purchase-order decision centre for remaining listed rows, stale eBay sync context, and local review run creation.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadRows} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading && <LinearProgress />}

      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            size="small"
            label="PO filter"
            value={poFilter}
            onChange={(event) => setPoFilter(event.target.value.replace(/[^0-9]/g, ""))}
            helperText="Opened automatically from Purchase Orders links. Repricing does not mutate PO data."
          />
          <Button variant="outlined" onClick={() => setPoFilter("")} disabled={!poFilter}>
            Clear PO Filter
          </Button>
          <Button variant="contained" color="secondary" onClick={() => createPoCleanupRun(poFilter)} disabled={!poFilter || Boolean(creatingRunForPo)}>
            {creatingRunForPo ? "Creating..." : "Create PO Cleanup Run"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Showing {numberText(filteredRows.length)} of {numberText(rows.length)} PO row(s).
          </Typography>
        </Stack>
      </Paper>

      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${numberText(summary.po_count)} POs`} />
          <Chip label={`${money(summary.total_po_cost)} PO cost`} />
          <Chip label={`${money(summary.total_net_revenue_estimate)} net revenue est.`} />
          <Chip label={`${money(summary.total_remaining_stock_value)} remaining value`} />
          <Chip label={`${numberText(summary.remaining_listed_count)} remaining listed`} />
          <Chip label={`${numberText(summary.stale_ebay_sync_remaining_count)} stale eBay sync`} />
          <Chip label={`${numberText(summary.po_auto_candidate_count)} auto rows`} />
          <Chip label={`${numberText(summary.po_manual_review_count)} manual rows`} />
        </Stack>
      )}

      <Paper variant="outlined" sx={{ height: 560, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.purchase_order_id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "purchase_order_id", sort: "desc" }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
